import React, { useId } from "react";
import {
  toRad,
  toDeg,
  gradientPicker,
  elevationToHorizon,
  angularDist,
  isAboveHorizon,
} from "../Utils";
import {
  getMoonPhase,
  moonLitPath,
  moonVisibilityOpacity,
  moonDaylightFade,
} from "../moonPhase";

// The Sun and Moon travel on a circle inside the celestial objects circle,
// at this fraction of rCelestials (the sky area above the horizon).
const SKY_RADIUS_FACTOR = 0.6;

// Apparent size grows toward the horizon (the moon illusion) and the colour
// shifts over the same elevation band the horizon uses for sunrise/sunset.
const HORIZON_EFFECT_DEG = 18;
const MAX_SIZE_SCALE = 1.3;

// Glow radii relative to the body's disc radius. The Moon's glow is smaller
// and fainter than the Sun's.
const SUN_GLOW_RADIUS = 4.5;
const MOON_GLOW_RADIUS = 3.0;

// The Moon is offset from the Sun's circle by its ecliptic latitude relative
// to the Sun: px per degree of latitude at the 600 px design size.
const LATITUDE_PX_PER_DEG = 7.2;

// Reference diameters at the design canvas size (600 px); scaled by canvas.
const SUN_SIZE = 65;
const MOON_SIZE = 58;

const SUN_BASE_COLOR = "#FFD766"; // warm yellow high in the sky
const SUN_HORIZON_COLOR = "#FFA726"; // warm orange near the horizon
const MOON_LIT_BASE_COLOR = "#E3E0D8"; // pale grey high in the sky
const MOON_LIT_HORIZON_COLOR = "#F3DFA0"; // warm yellow near the horizon
const MOON_DARK_COLOR = "#0C0E12"; // night side at full moon (no earthshine)
const MOON_EARTHSHINE_COLOR = "#484C54"; // night side at new moon (earthshine)

interface Props {
  sunLon: number;
  moonLon: number;
  sunLat: number;
  moonLat: number;
  west: number;
  east: number;
  rCelestials: number;
  canvas: number[];
}

// The glows re-mount with a short fade-in after being hidden, so they never
// pop in abruptly once an interpolation finishes.
const GLOW_FADE_IN = { animation: "sun-moon-glow-in 0.8s ease-out" };

/**
 * The Sun and Moon as discs in the sky above the horizon, with the Moon drawn
 * in its current phase. Rendered in screen space (outside the rotating group),
 * so it is placed with the same `west` the horizon uses.
 */
const SunMoon: React.FC<Props> = ({
  sunLon,
  moonLon,
  sunLat,
  moonLat,
  west,
  east,
  rCelestials,
  canvas,
}) => {
  const cx = canvas[0] / 2;
  const cy = canvas[1] / 2;
  const uid = useId().replace(/[:]/g, "");
  const glowId = `sun-glow-${uid}`;
  const moonGlowId = `moon-glow-${uid}`;
  const sunShadeId = `sun-shade-${uid}`;
  const moonShadeId = `moon-shade-${uid}`;
  const skyClipId = `sky-clip-${uid}`;
  const litClipId = `moon-lit-${uid}`;

  const rSky = rCelestials * SKY_RADIUS_FACTOR;
  const sizeFactor = 600 / Math.min(canvas[0], canvas[1]);

  // The Moon travels on its own circle, offset from the Sun's circle by its
  // ecliptic latitude relative to the Sun. It is usually above or below the
  // Sun's path and only crosses it at conjunctions (when eclipses can occur).
  const moonRadius =
    rSky + (moonLat - sunLat) * LATITUDE_PX_PER_DEG * sizeFactor;

  // The horizon is the horizontal line through the centre of the screen; the
  // sky layer is drawn outside the rotating group, so a body at ecliptic
  // longitude `lon` is placed on the rSky circle rotated by -(west + 180).
  const screenPos = (lon: number, r: number) => {
    const rad = toRad(lon);
    const dx = r * Math.cos(rad);
    const dy = r * Math.sin(rad);
    const rot = toRad(-west - 180);
    const cosR = Math.cos(rot);
    const sinR = Math.sin(rot);
    return {
      x: cx + dx * cosR - dy * sinR,
      y: cy + dx * sinR + dy * cosR,
    };
  };

  const sunPos = screenPos(sunLon, rSky);
  const moonPos = screenPos(moonLon, moonRadius);

  const sunElev = Math.min(
    elevationToHorizon(sunLon, west),
    HORIZON_EFFECT_DEG,
  );
  const moonElev = Math.min(
    elevationToHorizon(moonLon, west),
    HORIZON_EFFECT_DEG,
  );

  const sunScale =
    1 + (MAX_SIZE_SCALE - 1) * (1 - sunElev / HORIZON_EFFECT_DEG);
  const moonScale =
    1 + (MAX_SIZE_SCALE - 1) * (1 - moonElev / HORIZON_EFFECT_DEG);

  const sunColor = gradientPicker(
    SUN_HORIZON_COLOR,
    SUN_BASE_COLOR,
    sunElev,
    [0, HORIZON_EFFECT_DEG],
  );
  const moonLitColor = gradientPicker(
    MOON_LIT_HORIZON_COLOR,
    MOON_LIT_BASE_COLOR,
    moonElev,
    [0, HORIZON_EFFECT_DEG],
  );

  const sunR = ((sizeFactor * SUN_SIZE) / 2) * sunScale;
  const moonR = ((sizeFactor * MOON_SIZE) / 2) * moonScale;

  // A body stays visible while any part of its disc is still above the
  // horizon line (the sky clip below hides the below-horizon part, so it
  // sinks behind the horizon and only disappears once fully below).
  const sunVisible = sunPos.y - sunR < cy;
  const moonVisible = moonPos.y - moonR < cy;

  // The Moon always has two parts: the Sun-lit part and the night part. The
  // night part's brightness follows the earthshine strength (brightest at new
  // moon, nearly black at full moon).
  const phase = getMoonPhase(sunLon, moonLon);
  const lit = moonLitPath(moonR, phase.phase);
  const moonNightColor = gradientPicker(
    MOON_DARK_COLOR,
    MOON_EARTHSHINE_COLOR,
    phase.earthshine,
    [0, 1],
  );

  // Orient the Moon so its lit side faces the Sun's actual sky position: the
  // terminator (and the crescent's horn line) stays perpendicular to the line
  // joining the centres of the Sun and Moon.
  const phaseRotation = toDeg(
    Math.atan2(sunPos.y - moonPos.y, sunPos.x - moonPos.x),
  );

  // The Moon fades out as it approaches the Sun in the sky, and daylight adds
  // extra transparency. Both apply to the disc and its glow.
  const sunIsUp = isAboveHorizon(sunLon, west, east);
  const daylightFade = moonDaylightFade(
    sunIsUp,
    elevationToHorizon(sunLon, west),
  );
  const moonOpacity =
    moonVisibilityOpacity(angularDist(sunLon, moonLon)) * (1 - daylightFade);

  return (
    <g data-testid="sun-moon" style={{ pointerEvents: "none" }}>
      <defs>
        <style>{`@keyframes sun-moon-glow-in { from { opacity: 0; } to { opacity: 1; } }`}</style>
        <clipPath id={skyClipId}>
          <polygon
            points={`0,0 ${canvas[0]},0 ${canvas[0]},${cy} 0,${cy}`}
          />
        </clipPath>
        <radialGradient id={glowId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={sunColor} stopOpacity="0.35" />
          <stop offset="25%" stopColor={sunColor} stopOpacity="0.2" />
          <stop offset="50%" stopColor={sunColor} stopOpacity="0.09" />
          <stop offset="75%" stopColor={sunColor} stopOpacity="0.03" />
          <stop offset="100%" stopColor={sunColor} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={moonGlowId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={moonLitColor} stopOpacity="0.09" />
          <stop offset="33%" stopColor={moonLitColor} stopOpacity="0.14" />
          <stop offset="60%" stopColor={moonLitColor} stopOpacity="0.06" />
          <stop offset="100%" stopColor={moonLitColor} stopOpacity="0" />
        </radialGradient>
        {/* Globe shading: brighter core, darker limb. */}
        <radialGradient id={sunShadeId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.18" />
          <stop offset="55%" stopColor="#FFFFFF" stopOpacity="0" />
          <stop offset="75%" stopColor="#000000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.28" />
        </radialGradient>
        <radialGradient id={moonShadeId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#000000" stopOpacity="0" />
          <stop offset="70%" stopColor="#000000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.2" />
        </radialGradient>
        <clipPath id={litClipId}>
          <path d={lit.d} fillRule={lit.fillRule} />
        </clipPath>
      </defs>

      {/* Glows stay outside the sky clip so they fade out smoothly to
          complete transparency instead of being sliced at the horizon. */}
      {sunVisible && (
        <g
          data-testid="sun-glow"
          style={GLOW_FADE_IN}
          transform={`translate(${sunPos.x}, ${sunPos.y})`}
        >
          <circle r={sunR * SUN_GLOW_RADIUS} fill={`url(#${glowId})`} />
        </g>
      )}

      <g data-testid="sun-moon-sky" clipPath={`url(#${skyClipId})`}>
        {sunVisible && (
          <g
            data-testid="sun-sky"
            transform={`translate(${sunPos.x}, ${sunPos.y})`}
          >
            <circle r={sunR} fill={sunColor} />
            <circle r={sunR} fill={`url(#${sunShadeId})`} />
          </g>
        )}
        {moonVisible && (
          <g
            data-testid="moon-sky"
            opacity={moonOpacity}
            transform={`translate(${moonPos.x}, ${moonPos.y}) rotate(${phaseRotation})`}
          >
            <circle
              r={moonR}
              fill={moonNightColor}
              stroke="rgba(0,0,0,0.18)"
              strokeWidth={0.75}
            />
            <path d={lit.d} fillRule={lit.fillRule} fill={moonLitColor} />
            {/* Faint craters, clipped to the lit region only */}
            <g clipPath={`url(#${litClipId})`}>
              <circle
                cx={-moonR * 0.28}
                cy={-moonR * 0.18}
                r={moonR * 0.2}
                fill="#000"
                opacity={0.09}
              />
              <circle
                cx={moonR * 0.22}
                cy={moonR * 0.32}
                r={moonR * 0.14}
                fill="#000"
                opacity={0.1}
              />
              <circle
                cx={-moonR * 0.02}
                cy={-moonR * 0.38}
                r={moonR * 0.1}
                fill="#000"
                opacity={0.07}
              />
            </g>
            {/* Slightly darker limb for a spherical look */}
            <circle r={moonR} fill={`url(#${moonShadeId})`} />
          </g>
        )}
      </g>

      {/* The Moon's glow is drawn after the Moon (including the night side)
          so it stays visible on top of the dark part. */}
      {moonVisible && (
        <g
          data-testid="moon-glow"
          style={GLOW_FADE_IN}
          opacity={moonOpacity}
          transform={`translate(${moonPos.x}, ${moonPos.y}) rotate(${phaseRotation})`}
        >
          <circle r={moonR * MOON_GLOW_RADIUS} fill={`url(#${moonGlowId})`} />
        </g>
      )}
    </g>
  );
};

export default React.memo(SunMoon);
